import type { AdminAgentApprovalProps } from "../models/admin-agent.models";
import AdminAgentSqlReview from "./AdminAgentSqlReview";

// Permission covers the displayed call only. The backend binds the decision to its original arguments and caller.
const AdminAgentApproval = ({ approval, busy, onDecide }: AdminAgentApprovalProps) => {
  const expired = Date.parse(approval.expiresAt) <= Date.now();
  const sqlReview = approval.toolName === "debug_sql" && Boolean(approval.sqlContext);

  return (
    <section
      aria-label={sqlReview ? "SQL execution permission" : "OGA tool permission"}
      data-test="admin-agent-oga-approval"
      className="shrink-0 border-y border-amber-300 bg-amber-50 p-3 text-xs text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
    >
      {!sqlReview && (
        <p className="font-semibold" data-test="admin-agent-oga-category">
          {approval.category} · {approval.subcategory}
        </p>
      )}
      <p className="mt-1 font-semibold">{sqlReview ? "Run this SQL?" : `Allow ${approval.toolName}?`}</p>
      {sqlReview ? (
        <AdminAgentSqlReview approval={approval} />
      ) : (
        <>
          <p className="mt-1">
            {approval.readOnly ? "Retrieval and analysis." : "This agent may change data."} OGA may run several paid
            model and tool calls; the total cost is not known in advance.
          </p>
          <pre
            data-test="admin-agent-oga-approval-arguments"
            className="my-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-white/60 p-2 dark:bg-black/20"
          >
            {JSON.stringify(approval.arguments, null, 2)}
          </pre>
        </>
      )}
      <p>
        {expired
          ? "This request expired. Stop it or send a new instruction."
          : sqlReview
            ? "Not run yet. Approval covers this batch only."
            : "Approval is for this exact call only. Every later OGA call asks again."}
      </p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-test="admin-agent-oga-approve"
          disabled={busy || expired}
          onClick={() => void onDecide("approve")}
          className="rounded bg-green px-3 py-1.5 font-semibold text-white disabled:opacity-50"
        >
          {sqlReview ? "Run SQL" : "Allow this call"}
        </button>
        <button
          type="button"
          data-test="admin-agent-oga-decline"
          disabled={busy || expired}
          onClick={() => void onDecide("decline")}
          className="rounded border border-amber-500 px-3 py-1.5 font-semibold disabled:opacity-50"
        >
          Decline
        </button>
      </div>
    </section>
  );
};

export default AdminAgentApproval;
